import React from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import {bindActionCreators} from 'redux';
import {showApplyPipettingConfirmationModal} from 'stores/business/pipettingParameterBusiness';
import {CopyPipettingValueMode} from 'constants/cardStateEnum';
import ContextMenu from '../../../../components/ContextMenu/ContextMenu';
import withIntl from '../../../../lib/withIntl';
import {
  copyPipettingParameterCard,
  pastePipettingParameterCard,
  showApplyPipettingConfirmationAndPasteToCard,
} from '../../../../stores/business/protocolEditorBusiness';
import {CardType, TaskType} from '../../../../constants/dataEnum';

function mapStateToProps(state) {
  const {copiedPipettingParameter} = state.protocolEditorState;
  return {
    protocolEditorCopiedPipettingParameterData: copiedPipettingParameter,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    protocolEditorBusinessCopyPipettingParamCard: bindActionCreators(copyPipettingParameterCard, dispatch),
    protocolEditorBusinessPastePipettingParamCard: bindActionCreators(
      showApplyPipettingConfirmationAndPasteToCard,
      dispatch,
    ),
  };
}

const CardContextMenu = React.forwardRef(
  (
    {
      intl,
      onDuplicate,
      onGroup,
      onUngroup,
      onDelete,
      onCheck,
      onUncheck,
      hideContextMenu,
      isDisableContexMenu,
      isGroup,
      isDisableGroup,
      isDisableDuplicate = false,
      isDisableDelete = false,
      isDisableCheck = false,
      isDisableUncheck = false,
      isMultipleCardSelected,
      isDisablePastePipettingParameterProps,
      isDisableCopyPipettingParameterProps,
      onCopyPipettingParameter,
      onPastePipettingParameter,
      protocolEditorBusinessCopyPipettingParamCard,
      protocolEditorCopiedPipettingParameterData,
      card,
    },
    ref,
  ) => {
    const dispatch = useDispatch();

    const handleCopyPipettingParameter = async () => {
      if (card?.tasks) {
        await protocolEditorBusinessCopyPipettingParamCard(card.tasks);
        if (onCopyPipettingParameter) onCopyPipettingParameter();
      }
    };

    const handlePastePipettingParameter = async () => {
      if (card?.tasks) {
        const callback = () => {
          dispatch(pastePipettingParameterCard(card));
          if (onPastePipettingParameter) onPastePipettingParameter();
        };

        dispatch(showApplyPipettingConfirmationModal(CopyPipettingValueMode.TO_SINGLE_CARD, callback));
      }
    };

    const isCopyPipettingParameterCardDisabled = () => {
      if (!card?.cardType) return true;

      const hasAspirateDispenseTask = card.tasks.find(
        (task) => task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE,
      );

      if (isMultipleCardSelected || card.cardType !== CardType.SIMPLE_TRANSFER || !hasAspirateDispenseTask) {
        return true;
      }

      return false;
    };

    const isPastePipettingParameterCardDisabled = () => {
      if (isMultipleCardSelected || !card?.cardType) {
        return true;
      }

      if (card?.cardType) {
        const hasAspirateDispenseTask = card.tasks.find(
          (task) => task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE,
        );

        if (card.cardType !== protocolEditorCopiedPipettingParameterData?.type || !hasAspirateDispenseTask) {
          return true;
        }
      }

      return false;
    };

    function renderSubContextMenu() {
      const contextMenuItem = [];
      let isDisablePastePipettingParam = isDisablePastePipettingParameterProps;
      let isDisableCopyPipettingParam = isDisableCopyPipettingParameterProps;

      if (isDisableCopyPipettingParam == null) {
        isDisableCopyPipettingParam = isCopyPipettingParameterCardDisabled();
      }

      if (isDisablePastePipettingParam == null) {
        isDisablePastePipettingParam = isPastePipettingParameterCardDisabled();
      }

      if (!isDisableDuplicate) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={onDuplicate}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-duplicate icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.cardMenuContex.duplicate'})}
          />,
        );
      }

      if (!isDisableCopyPipettingParam) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={handleCopyPipettingParameter}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-copy icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.menuContext.copy'})}
          />,
        );
      }

      if (!isDisablePastePipettingParam) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={handlePastePipettingParameter}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-paste icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.menuContext.paste'})}
          />,
        );
      }
      if (!isGroup && !isDisableGroup) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={onGroup}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-grouping icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.cardMenuContex.group'})}
            disabled={isDisableGroup}
          />,
        );
      }
      if (isGroup) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={onUngroup}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-ungrouping icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.cardMenuContex.ungroup'})}
          />,
        );
      }
      if (!isDisableCheck) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={onCheck}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-check icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.cardMenuContex.check'})}
          />,
        );
      }
      if (!isDisableUncheck) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={onUncheck}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-uncheck icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.cardMenuContex.uncheck'})}
          />,
        );
      }
      if (!isDisableDelete) {
        contextMenuItem.push(
          <ContextMenu.Button
            onClick={onDelete}
            hideContextMenu={hideContextMenu}
            glyphicon="icon-trash icon"
            text={intl.formatMessage({id: 'protocolEditor.taskList.cardMenuContex.delete'})}
          />,
        );
      }
      return contextMenuItem;
    }

    return (
      <ContextMenu hideContextMenu={hideContextMenu} ref={ref} isDisable={isDisableContexMenu}>
        {renderSubContextMenu()}
      </ContextMenu>
    );
  },
);

CardContextMenu.propTypes = {
  intl: PropTypes.any.isRequired,
  onDuplicate: PropTypes.func,
  onGroup: PropTypes.func,
  onUngroup: PropTypes.func,
  onDelete: PropTypes.func,
  onCheck: PropTypes.func,
  onUncheck: PropTypes.func,
  hideContextMenu: PropTypes.func,
  onCopyPipettingParameter: PropTypes.func,
  onPastePipettingParameter: PropTypes.func,
  protocolEditorBusinessCopyPipettingParamCard: PropTypes.func.isRequired,
  isDisableContexMenu: PropTypes.bool,
  isGroup: PropTypes.bool,
  isDisableGroup: PropTypes.bool,
  isDisableDuplicate: PropTypes.bool,
  isDisableDelete: PropTypes.bool,
  isDisableCheck: PropTypes.bool,
  isDisableUncheck: PropTypes.bool,
  isDisableCopyPipettingParameterProps: PropTypes.bool,
  isDisablePastePipettingParameterProps: PropTypes.bool,
  isMultipleCardSelected: PropTypes.bool,
  card: PropTypes.object,
  protocolEditorCopiedPipettingParameterData: PropTypes.object.isRequired,
};

CardContextMenu.defaultProps = {
  onDuplicate: () => {},
  onGroup: () => {},
  onUngroup: () => {},
  onDelete: () => {},
  onCheck: () => {},
  onUncheck: () => {},
  hideContextMenu: () => {},
  onCopyPipettingParameter: () => {},
  onPastePipettingParameter: () => {},
  card: {},
  isDisableContexMenu: false,
  isGroup: false,
  isDisableGroup: false,
  isDisableDuplicate: false,
  isDisableDelete: false,
  isDisableCheck: false,
  isDisableUncheck: false,
  isDisableCopyPipettingParameterProps: null,
  isDisablePastePipettingParameterProps: null,
  isMultipleCardSelected: false,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(withIntl(CardContextMenu));
